// src/pages/BlogsPage.jsx
import React, { useState } from "react";
import { Btn, Badge, Modal, SectionHeader, EmptyState, InfoBanner } from "../components/UI";
import BlogForm from "../components/BlogForm";
import { deleteDocument } from "../hooks/useFirestore";
import { deleteFile } from "../utils/uploadFile";

export default function BlogsPage({ blogs, authors, categories, isAdmin }) {
  const [showForm, setShowForm] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [filter, setFilter] = useState("all");

  const filtered = filter === "all" ? blogs : blogs.filter(b => b.status === filter);

  const handleDelete = async (blog) => {
    if (!window.confirm(`Delete "${blog.title}"? This cannot be undone.`)) return;
    try {
      // Delete media files from Storage
      if (blog.thumbnailURL) await deleteFile(blog.thumbnailURL);
      for (const topic of blog.topics || []) {
        if (topic.imageURL) await deleteFile(topic.imageURL);
      }
      await deleteDocument("blogs", blog.id);
    } catch (err) {
      console.error("Delete error:", err);
      alert("Delete failed. Check console for details.");
    }
  };

  const handleEdit = (blog) => {
    setEditTarget(blog);
    setShowForm(true);
  };

  return (
    <div>
      {!isAdmin && (
        <InfoBanner type="warning">
          👁 View-only mode. Contact an Admin to get editing access.
        </InfoBanner>
      )}

      <SectionHeader
        title="Blogs"
        action={isAdmin && (
          <Btn onClick={() => { setEditTarget(null); setShowForm(true); }}>
            + New Blog
          </Btn>
        )}
      />

      {/* Filter tabs */}
      <div style={{ display: "flex", gap: 8, marginBottom: 18 }}>
        {["all", "published", "draft"].map(f => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            style={{
              padding: "6px 16px", borderRadius: 99, fontSize: 12, fontWeight: 600,
              border: `1px solid ${filter === f ? "#2d7a3a" : "#ddd"}`,
              background: filter === f ? "#e8f5e9" : "#fff",
              color: filter === f ? "#2d7a3a" : "#666",
              cursor: "pointer", fontFamily: "inherit", textTransform: "capitalize",
            }}
          >
            {f === "all" ? `All (${blogs.length})` : f === "published"
              ? `Published (${blogs.filter(b => b.status === "published").length})`
              : `Drafts (${blogs.filter(b => b.status === "draft").length})`}
          </button>
        ))}
      </div>

      {/* Blog list */}
      <div style={{ display: "grid", gap: 12 }}>
        {filtered.length ? filtered.map(blog => (
          <div key={blog.id} style={{
            background: "#fff", border: "1px solid #e8f0e9", borderRadius: 12,
            padding: 16, display: "flex", gap: 14, alignItems: "flex-start",
          }}>
            {/* Thumbnail */}
            {blog.thumbnailURL ? (
              <img src={blog.thumbnailURL} alt="" style={{ width: 90, height: 65, borderRadius: 8, objectFit: "cover", flexShrink: 0 }} />
            ) : (
              <div style={{ width: 90, height: 65, borderRadius: 8, background: "#e8f5e9",
                display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24, flexShrink: 0 }}>📝</div>
            )}

            {/* Info */}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4, flexWrap: "wrap" }}>
                <span style={{ fontSize: 15, fontWeight: 700, color: "#1a1a1a" }}>{blog.title}</span>
                <Badge color={blog.status === "published" ? "#2d7a3a" : "#c05621"}>{blog.status || "draft"}</Badge>
                {blog.category && <Badge color="#2b6cb0">{blog.category}</Badge>}
              </div>
              <p style={{ margin: "0 0 6px", fontSize: 13, color: "#666" }}>{blog.subheading}</p>
              <div style={{ fontSize: 12, color: "#999" }}>
                📅 {blog.date}
                {blog.authorId && authors.find(a => a.id === blog.authorId) &&
                  ` · ✍️ ${authors.find(a => a.id === blog.authorId).name}`}
                {` · 📌 ${blog.topics?.length || 0} topic${blog.topics?.length !== 1 ? "s" : ""}`}
              </div>
            </div>

            {/* Actions (Admin only) */}
            {isAdmin && (
              <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                <Btn variant="ghost" style={{ fontSize: 12, padding: "6px 12px" }} onClick={() => handleEdit(blog)}>
                  ✏️ Edit
                </Btn>
                <Btn variant="danger" style={{ fontSize: 12, padding: "6px 12px" }} onClick={() => handleDelete(blog)}>
                  🗑 Delete
                </Btn>
              </div>
            )}
          </div>
        )) : (
          <EmptyState icon="📝" message={isAdmin ? "No blogs yet. Click '+ New Blog' to create one." : "No blogs available."} />
        )}
      </div>

      {/* Modal */}
      {showForm && (
        <Modal
          title={editTarget ? `Edit: ${editTarget.title}` : "Create New Blog"}
          onClose={() => { setShowForm(false); setEditTarget(null); }}
          wide
        >
          <BlogForm
            onSave={() => { setShowForm(false); setEditTarget(null); }}
            onClose={() => { setShowForm(false); setEditTarget(null); }}
            authors={authors}
            categories={categories}
            isAdmin={isAdmin}
            existing={editTarget}
          />
        </Modal>
      )}
    </div>
  );
}
